import axios from "axios";

export type TripStatus =
  | "created"
  | "searching"
  | "assigned"
  | "started"
  | "waiting"
  | "carrying"
  | "arrived"
  | "finished"
  | "cancelled"
  | "emergency"
  | "autocancelled"
  | "rejected"
  | "unassigned"
  | "retrieved";

export interface TrackingDriver {
  name?: string;
  rating?: string;
  photoUrl?: string;
}

export interface TrackingVehicle {
  brand?: string;
  model?: string;
  patent?: string;
  color?: string;
  /** Latitud actual del conductor. Disponible solo cuando hay conductor asignado. */
  lat?: number;
  /** Longitud actual del conductor. Disponible solo cuando hay conductor asignado. */
  lng?: number;
}

export interface TrackingStatus {
  status: TripStatus;
  driver: TrackingDriver;
  vehicle: TrackingVehicle;
  /** Polyline encodeada (formato Google) del recorrido origen → destino. */
  polyline?: string;
  /**
   * Polyline encodeada (formato Google) desde la posición del conductor hasta el
   * origen. Se recalcula en cada GET mientras el conductor va al punto de encuentro.
   */
  polylineStarted?: string;
  /** Tiempo estimado de llegada en minutos. */
  etaMinutes?: number;
  /** Intervalo de polling recomendado en ms. null => estado terminal, dejar de pollear. */
  pollingIntervalMs: number | null;
}

export interface TrackTripResponse {
  success: boolean;
  status?: number;
  message?: string;
  data?: TrackingStatus;
}

export type TrackTripErrorReason = "not-found" | "expired" | "unknown";

export class TrackTripError extends Error {
  reason: TrackTripErrorReason;
  constructor(reason: TrackTripErrorReason, message?: string) {
    super(message ?? reason);
    this.name = "TrackTripError";
    this.reason = reason;
  }
}

const toTrackTripError = (status?: number, message?: string) => {
  if (status === 404) return new TrackTripError("not-found");
  if (status === 410) return new TrackTripError("expired");
  return new TrackTripError("unknown", message);
};

export const useTrackTripService = () => {
  const apiUrl = process.env.REACT_APP_BASE_URL;

  const trackTrip = async (token: string): Promise<TrackingStatus> => {
    let response: TrackTripResponse | TrackingStatus;
    try {
      const { data } = await axios.get<TrackTripResponse | TrackingStatus>(
        `${apiUrl}/trips/trips/track/${encodeURIComponent(token)}`,
        { headers: { "Content-Type": "application/json" } },
      );
      response = data;
    } catch (error: any) {
      throw toTrackTripError(error?.response?.status, error?.response?.data?.message);
    }

    if (!("success" in response)) return response;
    if (response.success && response.data) return response.data;
    throw toTrackTripError(response.status, response.message);
  };

  return { trackTrip };
};
